import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User as UserIcon, Award, MapPin } from "lucide-react";
import { CATEGORIES } from "@/data/constants";
import CategoryIcon from "@/components/CategoryIcon";
import TierBadge from "@/components/TierBadge";

function formatMonthYear(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long" });
}

const TIER_ORDER = ["Diamond", "Platinum", "Gold", "Silver", "Bronze"];
const TIER_COLOUR = {
  Diamond: "bg-indigo-600 text-white",
  Platinum: "bg-sky-700 text-white",
  Gold: "bg-amber-500 text-white",
  Silver: "bg-slate-500 text-white",
  Bronze: "bg-amber-700 text-white",
};

export default function Nominator() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/nominators/${id}`);
        setProfile(data);
      } catch (e) {
        setError(e.response?.status === 404 ? "This nominator profile does not exist." : "Could not load profile.");
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="min-h-[40vh] flex items-center justify-center text-gray-500">Loading…</div>;
  if (error || !profile) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <p className="text-lg text-gray-700">{error || "Not found"}</p>
      <Button asChild className="mt-4 rounded-full"><Link to="/search">Explore venues</Link></Button>
    </div>
  );

  const t = profile.totals || {};
  const totalApproved = t.approved || 0;
  const tierCounts = t.tier_counts || {};

  return (
    <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="nominator-profile">
      <Link to="/search" className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-blue-700">
        <ArrowLeft className="w-4 h-4"/> Back to venues
      </Link>

      <header className="mt-6 flex flex-wrap items-start gap-6">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center font-heading font-bold text-3xl shadow-lg">
          <UserIcon className="w-9 h-9" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-blue-700 font-semibold">
            <Award className="w-4 h-4"/> Nominator
          </div>
          <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-bold text-gray-900" data-testid="nominator-name">{profile.name}</h1>
          {profile.member_since && (
            <p className="mt-1 text-sm text-gray-600">Member since {formatMonthYear(profile.member_since)}</p>
          )}
        </div>
      </header>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Venues nominated" value={totalApproved} testid="nominator-stat-approved"/>
        <StatCard label="Inspected so far" value={t.inspected || 0} testid="nominator-stat-inspected"/>
        <StatCard label="Average score" value={t.avg_rating > 0 ? `${t.avg_rating}/100` : "—"} testid="nominator-stat-avg"/>
        <StatCard label="Diamond venues" value={tierCounts.Diamond || 0} testid="nominator-stat-diamond"/>
      </div>

      {Object.keys(tierCounts).length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {TIER_ORDER.filter(tier => tierCounts[tier]).map(tier => (
            <Badge key={tier} className={`rounded-full ${TIER_COLOUR[tier] || "bg-gray-800 text-white"}`}>
              {tierCounts[tier]} × {tier}
            </Badge>
          ))}
        </div>
      )}

      <section className="mt-12">
        <h2 className="font-heading text-2xl font-semibold text-gray-900">
          Venues nominated
        </h2>
        {profile.venues.length === 0 ? (
          <p className="mt-4 text-gray-600">No approved nominations yet.</p>
        ) : (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="nominator-venue-list">
            {profile.venues.map(v => {
              const cat = CATEGORIES.find(c => c.slug === v.category);
              return (
                <Link key={v.id} to={`/venue/${v.id}`} className="block group">
                  <Card className="border border-gray-200 rounded-xl group-hover:border-gray-900 transition-colors duration-200">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 border border-blue-100 flex items-center justify-center">
                          <CategoryIcon slug={v.category} className="w-5 h-5"/>
                        </div>
                        {(v.rating_count || 0) > 0 || v.tier
                          ? <TierBadge points={v.rating} tier={v.tier} />
                          : <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Awaiting</span>}
                      </div>
                      <h3 className="mt-3 font-heading text-lg font-semibold text-gray-900 group-hover:text-blue-700">{v.venue_name}</h3>
                      <div className="mt-1 text-xs text-gray-600 inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3"/> {v.area} · {cat?.short || v.category}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </article>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <Card className="border border-gray-200 rounded-xl">
      <CardContent className="p-4">
        <div className="text-2xl font-heading font-bold text-gray-900" data-testid={testid}>{value}</div>
        <div className="text-xs text-gray-600 mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}
